/**
 * Catálogo de molduras de avatar (frente G — slots de equipar).
 *
 * Fonte ÚNICA do visual de cada moldura. O backend só guarda o id equipado em
 * `users.activeAvatarBorder` (client-locked, via setActiveCosmetic
 * kind="avatar_border"); o estilo vive aqui. Mantém alinhado com os store_items
 * `avatar_border` do seed (scripts/seed-store-items + seed-store-items-prod).
 *
 * Cores em hex/rgba puros (sem oklch/vars) — seguros para html-to-image. É a
 * exceção declarada à regra "zero cor hardcoded" da skill ui-tailwind: moldura
 * é item de catálogo com identidade de cor própria, não superfície temável.
 *
 * ── Progressão por raridade (decisão do fundador, 03/08) ────────────────────
 * A moldura NÃO é só uma string de cor: o tratamento escala com o preço. Antes
 * desta onda as 6 eram mecanicamente idênticas — a de 300 moedas e a de 1200
 * recebiam o mesmo anel de 3px, e a raridade só pintava um selo de texto na
 * loja.
 *
 *   comum     → anel estático
 *   raro      → anel + halo (glow)
 *   épico     → anel + halo + varredura de luz girando (sweep/ember)
 *   lendário  → tudo acima + faíscas orbitando (aurora)
 *
 * ⚠️ Invariante de economia: a Lendária é CONQUISTADA (60 dias de ofensiva) e
 * precisa ficar acima da moldura mais cara comprável. Se algum dia entrar uma
 * moldura paga com `aurora`, a Lendária sobe junto — senão o loop
 * badge→cosmético perde o prêmio e a ofensiva de 60 dias deixa de valer a pena.
 *
 * ── Founder sai da escada, não sobe nela (decisão do fundador, 05/08) ───────
 * `obsidian` é a única moldura que NÃO se compra com moedas nem se conquista
 * com ofensiva: chega por `subscriptions.isLifetime`. Ela se separa por MATÉRIA
 * (anel preto com o ouro entrando só como luz) em vez de "aurora + uma camada":
 * o teto de CAMADAS continua sendo o da Lendária, e é isso que preserva o
 * invariante acima. Item de R$249 passando à frente de 60 dias sem falhar
 * transformaria a conquista em segundo lugar.
 *
 * ⚠️ O nome do tratamento era `brass` até 18/08, quando a peça era latão
 * canelado. Ela foi reprovada por ser da mesma COR da Ouro — ver o item.
 */

/**
 * Tratamento visual da moldura. Define QUAIS camadas o anel ganha; a
 * velocidade/curva de cada uma mora nas classes de `styles/domain.css`.
 */
export type BorderEffect =
  | "glow"
  | "sweep"
  | "ember"
  | "aurora"
  | "obsidian"
  | "prism"
  | "bleed"
  | "misprint"
  | "stained"
  | "charge"
  | "facet";

export interface AvatarBorderItem {
  id: string;
  label: string;
  /**
   * Anel base, aplicado como `background` do wrapper. Continua sendo o
   * fallback fiel quando o efeito não roda: export via html-to-image e
   * `prefers-reduced-motion`.
   */
  gradient: string;
  /**
   * Camada girante (conic-gradient). Rotacionar um círculo com conic gira a
   * luz sem mexer no avatar. Só faz sentido com effect sweep/ember/aurora.
   */
  sweep?: string;
  /** Halo projetado — "a luz que sai da moldura". box-shadow completo. */
  glow?: string;
  effect?: BorderEffect;
  /** Espessura do anel em px. Default 3; épico/lendário usam 4. */
  ringPx?: number;
  /**
   * Camada FIXA pintada por cima da varredura — a luz que NÃO gira junto com o
   * metal. É o que separa "peça metálica girando sob uma lâmpada" de "faixa
   * colorida rodando": num objeto real o reflexo fica onde a luz está, e só a
   * textura passa por baixo dele. Sem isto, qualquer conic rotativo lê como
   * poste de barbearia por mais bem desenhado que seja.
   */
  specular?: string;
  /**
   * `box-shadow` inset do bisel — a aresta usinada do anel (luz em cima,
   * sombra embaixo). Não gira e não escala com o avatar de propósito: aresta
   * de metal tem espessura física, não proporcional.
   */
  edge?: string;
  /**
   * Cor do fio da aresta, para superfícies que precisam desenhá-lo em ESCALA
   * própria — hoje só o card exportado (`V3IdentityRow`).
   *
   * O `edge` acima é `box-shadow` em px absolutos (1px sobre um anel de 4px, no
   * avatar do app). No card o anel inteiro tem `m(3)` = 0,75px de nó: reusar o
   * `edge` ali desenharia uma aresta MAIS GROSSA que o próprio anel. Como a
   * string de box-shadow não é escalável sem reescrevê-la, o card monta o fio
   * dele a partir desta cor.
   *
   * ⚠️ É o fio que mantém a Obsidiana identificável quando ela é pequena: sem
   * ele, um anel preto sobre papel escuro some. Moldura sem `edgeColor`
   * simplesmente não desenha fio no card — nenhuma outra precisa.
   */
  edgeColor?: string;
  /**
   * Badge que destrava esta moldura — espelho de `store_items.requiresBadge`.
   * Mesma razão de estar aqui que em `flairsCatalog`: um catálogo, uma
   * pergunta. Travado contra o seed por `badgeRewardMirror.test.ts`.
   */
  requiresBadge?: string;
}

/**
 * Anel da Temporada 1 (Eclipse) — prêmio, nunca vendido. Opção A do mock
 * `redesign-livra/cosmeticos-t1` ("Totalidade"), escolhida pelo fundador 23/09.
 *
 * ⚠️ É o INVERSO da `border_sol` por construção: a Sol é anel CLARO com um arco
 * de sombra; esta é anel ESCURO (#1a1030, o disco ocultante) com DUAS coroas
 * acesas — a quente do Sol às 0° e a fria da Lua às 210°. A peça do prêmio
 * precisa ser reconhecível ao lado das duas que a Loja vende na mesma janela,
 * e matiz não basta a 28px; estrutura (onde fica a luz) basta.
 *
 * ⚠️ UMA arte, DUAS entradas (top 10 e Meta dos 40 dias): `requiresBadge` é
 * igualdade escalar em `grantBadgeFlairs` — "um cosmético, duas badges" são
 * dois `store_items` com o mesmo desenho (`league-season-goals.md` §1.6).
 * Quem ganha pelos dois caminhos fica com duas peças idênticas, sem problema.
 *
 * Raridade ÉPICA no seed — a mesma das duas badges (`badgeRewardLoop`: prêmio
 * nascido depois de 31/08 tem degrau igual ao da conquista). O que a separa da
 * Sol/Lua épicas da Loja é a estrutura e o fato de não se comprar.
 */
const ECLIPSE_T1_RING: Omit<AvatarBorderItem, "id" | "label" | "requiresBadge"> = {
  gradient:
    "linear-gradient(135deg, #2a2150 0%, #ffc46e 22%, #1a1030 50%, #a8b6f0 78%, #2a2150 100%)",
  sweep:
    "conic-gradient(from 0deg, #fff3dc 0deg, #ffc46e 26deg, #6b3a1c 56deg, #1a1030 90deg, #1a1030 124deg, #3b3a72 152deg, #a8b6f0 184deg, #f4f2ff 210deg, #a8b6f0 238deg, #3b3a72 266deg, #1a1030 296deg, #6b3a1c 322deg, #ffc46e 342deg, #fff3dc 360deg)",
  // Dois halos, um de cada coroa — é o que segura o anel escuro no papel escuro.
  glow: "0 0 12px rgba(255, 196, 110, 0.4), 0 0 26px rgba(168, 182, 240, 0.3)",
  // `sweep`, nunca `aurora` (Lendária) nem `specular`/`edge` (Founder).
  effect: "sweep",
  ringPx: 4,
};

/**
 * Anel da Temporada 2 (Entardecer — céu de verão) — prêmio, nunca vendido.
 * Opção A1 da rodada 2 do mock `redesign-livra/cosmeticos-t2`, escolhida pelo
 * fundador em 24/09 (a rodada 1 foi recusada pelas cores).
 *
 * Faixa de entardecer — laranja e rosa descendo para o violeta da noite — com
 * QUATRO pontos brancos: as estrelas do Cruzeiro do Sul. Estrutura própria: a
 * Sol da Loja (vendida até 30/11) é lisa e toda quente; o Eclipse da T1 é
 * escuro com duas coroas.
 *
 * ⚠️ Medido no render do mock: as estrelas quase somem a 52 e a 28 px — o que
 * separa o anel em tamanho de liga é a faixa rosa/violeta, não os pontos.
 * Implementado como aprovado; engrossar as estrelas é decisão de arte.
 *
 * Mesma regra da T1: UMA arte, DUAS entradas (top 10 e Meta dos 40 dias).
 */
const ENTARDECER_T2_RING: Omit<AvatarBorderItem, "id" | "label" | "requiresBadge"> = {
  gradient: "linear-gradient(180deg, #ff9a3c 0%, #ff4f6d 40%, #b0247a 70%, #3a1452 100%)",
  sweep:
    "conic-gradient(from 0deg, #3a1452 0deg, #3a1452 14deg, #ffffff 18deg, #3a1452 22deg, #b0247a 60deg, #ff4f6d 100deg, #ffffff 104deg, #ff4f6d 108deg, #ff9a3c 150deg, #ffd166 180deg, #ff9a3c 210deg, #ff4f6d 252deg, #ffffff 256deg, #ff4f6d 260deg, #b0247a 300deg, #3a1452 338deg, #ffffff 342deg, #3a1452 346deg, #3a1452 360deg)",
  glow: "0 0 14px rgba(255, 79, 109, 0.45), 0 0 28px rgba(255, 154, 60, 0.25)",
  // `sweep`, nunca `aurora` (Lendária) nem `specular`/`edge` (Founder).
  effect: "sweep",
  ringPx: 4,
};

export const AVATAR_BORDERS: AvatarBorderItem[] = [
  {
    id: "border_floral",
    label: "Floral",
    gradient: "linear-gradient(135deg, #f9a8d4, #4ade80)",
  },
  {
    id: "border_oceano",
    label: "Oceano",
    gradient: "linear-gradient(135deg, #3b82f6, #22d3ee)",
    glow: "0 0 10px rgba(34, 211, 238, 0.42), 0 0 22px rgba(59, 130, 246, 0.16)",
    effect: "glow",
  },
  {
    id: "border_neon",
    label: "Neon",
    gradient: "linear-gradient(135deg, #22d3ee, #d946ef)",
    glow: "0 0 11px rgba(217, 70, 239, 0.48), 0 0 24px rgba(34, 211, 238, 0.22)",
    effect: "glow",
  },
  {
    id: "border_brasa",
    label: "Brasa",
    gradient: "linear-gradient(135deg, #f97316, #ef4444, #fbbf24)",
    // Volta ao carvão escuro entre as chamas: é o contraste que faz ler como
    // brasa viva, e não como faixa laranja girando.
    sweep:
      "conic-gradient(from 0deg, #7c2d12, #ef4444, #fbbf24, #fde68a, #f97316, #b91c1c, #7c2d12)",
    glow: "0 0 12px rgba(249, 115, 22, 0.5), 0 0 26px rgba(239, 68, 68, 0.2)",
    effect: "ember",
  },
  {
    id: "border_ouro",
    label: "Ouro",
    gradient: "linear-gradient(135deg, #fbbf24, #f59e0b, #fde68a, #d97706)",
    // Dois realces especulares quase brancos (#fffbeb) separados por bronze
    // escuro (#a16207): é esse par claro/escuro que faz o anel ler como metal
    // polido em vez de gradiente amarelo. Item mais caro comprável da loja.
    sweep:
      "conic-gradient(from 0deg, #a16207, #d97706, #fbbf24, #fffbeb, #f59e0b, #b45309, #fde68a, #fffbeb, #d97706, #a16207)",
    glow: "0 0 12px rgba(251, 191, 36, 0.55), 0 0 30px rgba(245, 158, 11, 0.2)",
    effect: "sweep",
    ringPx: 4,
  },
  {
    id: "border_founder",
    label: "Founder",
    // ── OBSIDIANA (decisão do fundador, 18/08) ─────────────────────────────
    // A versão anterior era latão canelado — 24 pérolas girando sobre o mesmo
    // gradiente da FounderPlaque. Ela foi reprovada pelo motivo que o próprio
    // desenho não conseguia resolver: **ela e a Ouro são a mesma cor**. Duas
    // peças douradas com texturas diferentes continuam lendo como "as duas
    // douradas", e nos 30px da linha da liga a canelura vira serrilha e a
    // diferença some por completo. Medido lado a lado nos 3 tamanhos reais.
    //
    // A separação passou a ser por COR, não por textura: preto de cofre — o
    // mesmo par do tema Latão, que já é o tema exclusivo de Founder — com o
    // ouro entrando só como LUZ. É o vocabulário de joalheria (metal sobre
    // fundo escuro), e é inconfundível ao lado da Ouro em qualquer tamanho.
    //
    // ⚠️ O invariante de economia continua de pé, e por isso a peça NÃO ganhou
    // camada nenhuma: são as mesmas três do latão (varredura + luz fixa +
    // aresta). O teto de CAMADAS segue sendo o da Lendária, que é conquistada
    // com 60 dias de ofensiva e mantém as faíscas exclusivas. Um item de R$249
    // passando à frente disso transformaria a conquista em segundo lugar.
    // ⚠️ O anel base NÃO é preto puro, e isso saiu de medição. A 1ª versão da
    // obsidiana era `#241c15 → #120e0a → #2e2418 → #0f0c08` (luminância ~0,02
    // em todas as paradas): ao lado da Ouro e da Lendária ela lia como "aro
    // preto", não como metal escuro — o MESMO defeito pelo qual o latão v1 foi
    // reprovado, pela outra ponta. Bronze patinado nas paradas claras dá corpo
    // ao anel sem aproximá-lo do ouro: o que separa as duas continua sendo a
    // luminância, não o matiz.
    gradient:
      "linear-gradient(152deg, #4a3821 0%, #191108 30%, #5b452a 56%, #140e07 78%, #3d2e1a 100%)",
    // ── O lampejo de ouro ──────────────────────────────────────────────────
    // Dois arcos de luz sobre a obsidiana, e o resto do círculo TRANSPARENTE —
    // é o vazio entre eles que faz a peça ler como metal escuro polido em vez
    // de anel dourado com sombra. O arco principal (26-42°) tem o núcleo quase
    // branco (#fff8e1) que dá o "brilho de ponto"; o segundo (252-266°), na
    // diagonal oposta e com metade da força, é o reflexo secundário que
    // qualquer superfície curva tem. Girando devagar, os dois passam pela peça
    // como a luz passa por um anel real.
    //
    // ⚠️ Manter o `from 212deg`: é o que põe o arco principal no quadrante
    // superior-esquerdo em REPOUSO — que é onde a luz fixa (`specular`) também
    // está, e é o frame que sai congelado no card exportado.
    sweep:
      "conic-gradient(from 212deg, rgba(0,0,0,0) 0deg, rgba(201,163,85,.45) 10deg, rgba(232,207,154,.95) 24deg, #fff8e1 34deg, rgba(240,220,170,.85) 44deg, rgba(201,163,85,.4) 62deg, rgba(0,0,0,0) 84deg, rgba(0,0,0,0) 150deg, rgba(184,149,68,.32) 172deg, rgba(232,207,154,.6) 188deg, rgba(184,149,68,.28) 204deg, rgba(0,0,0,0) 226deg, rgba(0,0,0,0) 268deg, rgba(201,163,85,.4) 288deg, rgba(240,220,170,.7) 302deg, rgba(201,163,85,.35) 316deg, rgba(0,0,0,0) 336deg)",
    // Luz fixa — NÃO gira. Sobre a obsidiana ela é muito mais discreta que era
    // sobre o latão (0,16 contra 0,7): aqui ela não desenha o metal, só dá
    // volume ao preto e impede que ele leia como um buraco chapado.
    specular:
      "linear-gradient(150deg, rgba(255,255,255,.16) 0%, rgba(255,255,255,0) 34%, rgba(0,0,0,0) 70%, rgba(0,0,0,.28) 100%)",
    // Aresta usinada. O fio dourado de 1px em VOLTA (`0 0 0 1px`) é o que
    // mantém a peça identificável nos 30px da linha da liga, onde o arco de luz
    // é pequeno demais para carregar sozinho: naquele tamanho o que se vê é um
    // aro preto com contorno de ouro, e isso basta para não ser confundido com
    // avatar sem moldura.
    edge:
      "inset 0 0 0 1px rgba(232,207,154,0.55), inset 0 1px 0 rgba(255,248,225,0.35), inset 0 -1px 0 rgba(0,0,0,0.75)",
    edgeColor: "rgba(232,207,154,0.55)",
    // Halo quente e largo — a peça que não se compra não pode brilhar menos que
    // a Ouro de 1200 moedas. Sobre o anel escuro ele rende mais que rendia
    // sobre o latão, porque agora há contraste entre o halo e o próprio anel.
    glow: "0 0 14px rgba(232, 207, 154, 0.6), 0 0 34px rgba(201, 163, 85, 0.3)",
    // ── ⚠️ Como MEDIR esta peça (e por que a média engana) ──────────────────
    // O latão v1 foi reprovado por ter a luminância MÉDIA mais baixa das três
    // exclusivas. O critério estava certo lá — eram duas peças douradas, e a
    // mais cara não podia ser a mais apagada. Aplicado à obsidiana, ele mede a
    // coisa errada: média baixa é a DEFINIÇÃO de uma peça escura.
    //
    // Medido no pixel do anel (coroa 0,86–0,99 do raio, frame de repouso):
    //
    //   moldura    média    pico     contraste interno (pico ÷ média)
    //   Ouro       0,411    0,962    2,3×
    //   Obsidiana  0,159    0,929    5,8×   ← a que mais reluz
    //   Lendária   0,357    0,860    2,4×
    //
    // O critério para ela é o **pico** (o lampejo tem de bater o das outras, ou
    // ela não lê como metal) e o **contraste interno**. Quem for mexer aqui:
    // não "conserte" a média clareando o anel base — isso a devolve para a
    // família da Ouro, que é exatamente o que esta versão veio desfazer.
    effect: "obsidian",
    ringPx: 4,
  },
  // ── Exclusiva de PRO (27/08) ───────────────────────────────────────────────
  // Chega por `store_items.proExclusive` → `syncProCosmetics`, e SÓ entra no
  // inventário: assinar não troca o avatar de ninguém (decisão do fundador).
  // Perder o PRO desequipa, porque `revokeEquippedCosmetics` já trata
  // `activeAvatarBorder` — nenhuma linha de backend foi precisa.
  //
  // ⚠️ Sem FAÍSCAS, e isso é o invariante de economia, não economia de esforço:
  // a Lendária é conquistada com 60 dias de ofensiva e precisa continuar acima
  // de tudo que se paga. Ela se separa das compráveis por MATÉRIA — o mesmo
  // caminho da obsidiana do Founder — em vez de subir a escada de camadas.
  //
  // ⚠️ É UMA peça, e isso foi decisão do fundador depois de ver o render. A
  // versão de 27/08 trazia um par (Cristal + uma Prata metálica); a Prata foi
  // reprovada por ler como "anel desabilitado" e não como benefício — cinza é
  // a cor do neutro na UI, e em 28px não tem saturação para dizer outra coisa.
  // Escolher entre um item bom e um ruim não é escolha; uma peça só preserva
  // a identidade da assinatura em vez de diluí-la.
  {
    id: "border_cristal",
    label: "Cristal",
    // NÃO é metal — é vidro. Sai da escada de metais por completo, então não se
    // compara com Ouro nem com Founder. Usa o violeta que já identifica PRO no
    // anel de tier (`PRO_RING_STYLE`), de modo que quem vê reconhece a
    // assinatura sem precisar aprender uma cor nova.
    //
    // ⚠️ A Lendária TAMBÉM é violeta (#a78bfa + ciano), e ficar parecida com
    // ela seria o erro do latão v1 repetido. A separação é de matéria e de
    // temperatura: aqui o violeta é FRIO e translúcido, com paradas quase
    // transparentes entre os lampejos (vidro deixa passar); lá é opaco,
    // saturado e vem acompanhado de ciano e faíscas.
    gradient: "linear-gradient(135deg, #ddd6fe, #7c3aed, #e0e7ff, #5b21b6)",
    sweep:
      "conic-gradient(from 0deg, rgba(91,33,182,0.15), rgba(167,139,250,0.55), #f5f3ff, rgba(221,214,254,0.6), rgba(124,58,237,0.25), rgba(91,33,182,0.15))",
    // Idem: nada de `specular`/`edge`. Aqui custa ainda menos — vidro não tem
    // aresta usinada nem reflexo fixo de metal; o que faz o cristal ler como
    // cristal é a pulsação de opacidade do `prism` e as paradas translúcidas.
    glow: "0 0 13px rgba(167, 139, 250, 0.5), 0 0 30px rgba(124, 58, 237, 0.2)",
    effect: "prism",
    ringPx: 4,
  },
  // ─────────────────────────────────────────────────────────────────────────
  // Onda de 31/08 — matérias que o catálogo não tinha
  //
  // Diagnóstico do pedido ("bordas mais diferenciadas"): as cinco compráveis
  // até aqui são todas ANÉIS DE GRADIENTE. Floral, Oceano e Neon são o mesmo
  // objeto em três matizes; Brasa e Ouro acrescentam varredura. A progressão
  // de raridade era INTENSIDADE, não forma — por isso "mais uma cor" não
  // resolvia.
  //
  // ⚠️ A REGRA QUE REPROVOU TRÊS PROPOSTAS, e que vale para qualquer peça
  // nova: **num anel de 3-4px, textura não existe** — só cor, luz e divisão de
  // alto contraste. Medido no render, elemento repetido precisa de ~4px no
  // avatar de 28px (a linha da liga, onde a moldura mais aparece), o que dá
  // **≥18° por parada, ou no máximo ~20 elementos**. Abaixo disso vira
  // serrilha e a peça lê como anel QUEBRADO:
  //
  //   · "Louros" (24 folhas, 13° → 2,9px)      → reprovado pelo fundador
  //   · "Marginália" (rabisco, 11° → 0,85px)   → cortada no render
  //   · "Carimbo de Biblioteca" (tinta gasta)  → cortada: falha precisa de área
  //   · latão v1 (24 caneluras, 15° → 3,3px)   → o mesmo erro, em 18/08
  //
  // É a mesma conta em todos. Antes de propor peça com repetição, faça-a.
  {
    id: "border_capa_dura",
    label: "Capa Dura",
    // Tecido de encadernação com dois filetes de ouro. ZERO repetição — o
    // filete é 1px e por isso sobrevive a qualquer tamanho. Num catálogo em
    // que sete peças se mexem, ficar parada é o que a torna reconhecível.
    // ⚠️ O filete de ouro da 1ª versão era `edge`, e `specular`/`edge` são o
    // acabamento EXCLUSIVO da Founder (decisão de 18/08 — espalhá-lo apaga a
    // distinção dela). O ouro virou HALO, que é o tratamento que a escada de
    // raridade dá ao raro: a peça subiu de comum/300 para raro/600 junto.
    gradient: "linear-gradient(150deg, #2f5340 0%, #22402f 45%, #35604a 70%, #1d3626 100%)",
    glow: "0 0 10px rgba(214,178,102,.45), 0 0 22px rgba(160,124,58,.16)",
    effect: "glow",
  },
  {
    id: "border_grafite",
    label: "Grafite",
    // Substitui a "Marginália" (rabisco a lápis), cortada no render. A matéria
    // é a mesma — lápis —, resolvida por COR E LUZ em vez de traço: grafite
    // polido tem o brilho frio que um 6B pega de lado.
    gradient:
    // Sem `edge` (exclusivo da Founder) e sem halo: comum não ganha tratamento
    // nenhum pela escada. O brilho vem das paradas do próprio gradiente — o
    // par claro/escuro é o que faz ler como metal fosco em vez de cinza chapado.
      "linear-gradient(145deg, #8e96a0 0%, #3c4148 32%, #aab3bd 52%, #2b2f34 78%, #6d757e 100%)",
  },
  {
    id: "border_aquarela",
    label: "Aquarela",
    // ⚠️ A 1ª versão saía LAVADA e quase invisível: as paradas estavam em
    // opacidade .45–.5, e num anel fino isso vira bege sujo. Aquarela de
    // verdade é pigmento concentrado — o que dá a leitura de "aguada" é a
    // variação ENTRE as manchas, não a transparência de todas elas.
    gradient:
      "conic-gradient(from 35deg, #d1466a 0deg, #e8834f 70deg, #e3b93f 130deg, #3f9e8c 200deg, #4a6fb5 270deg, #8d5aa8 320deg, #d1466a 360deg)",
    // ⚠️ Sem varredura: comum não ganha tratamento (escada de raridade). A 1ª
    // versão tinha o efeito `bleed`, o que a punha no degrau do épico por
    // 350 moedas. Aquarela seca no papel não se mexe — a peça não perde nada.
  },
  {
    id: "border_riso",
    label: "Risografia",
    // Erro de registro como identidade: magenta e ciano LADO A LADO, com a
    // camada de cima deslocada ~1,5px. ⚠️ A 1ª versão usava
    // `mix-blend-mode: multiply` e as duas tintas se fundiram num azul-marinho
    // chapado — sumiu justamente o desalinhamento, que é a peça inteira.
    // ⚠️ Subiu de raro/600 para épico/1000: pela escada, raro é SÓ halo, e o
    // desalinhamento é uma camada animada — que é a definição do degrau épico.
    // Marcá-la rara exigiria tirar o offset, ou seja, tirar a peça.
    gradient: "conic-gradient(from 300deg, #e5397f 0deg 180deg, #f04f8d 180deg 360deg)",
    sweep: "conic-gradient(from 120deg, #29c3d6 0deg 170deg, rgba(0,0,0,0) 170deg 360deg)",
    effect: "misprint",
    glow: "0 0 11px rgba(229,57,127,.42), 0 0 24px rgba(41,195,214,.2)",
  },
  {
    id: "border_vitral",
    label: "Vitral",
    // A única peça com DIVISÃO INTERNA: 8 gomos separados por chumbo escuro,
    // 40° cada (11px a 28px, bem acima do piso). A luz atravessa pulsando por
    // segmento — é a diferença entre "luz passando por vidro" e "luz girando
    // num metal", que é o que a varredura do Ouro já faz.
    gradient:
      "conic-gradient(from 0deg, #c94f4f 0 40deg, #17120f 40deg 45deg, #3f7fb8 45deg 85deg, #17120f 85deg 90deg, #d9a441 90deg 130deg, #17120f 130deg 135deg, #4a9e6b 135deg 175deg, #17120f 175deg 180deg, #8b5aa8 180deg 220deg, #17120f 220deg 225deg, #c94f4f 225deg 265deg, #17120f 265deg 270deg, #3f7fb8 270deg 310deg, #17120f 310deg 315deg, #d9a441 315deg 355deg, #17120f 355deg 360deg)",
    sweep:
      "conic-gradient(from 90deg, rgba(255,245,210,.5), rgba(0,0,0,0) 30%, rgba(255,245,210,.35) 62%, rgba(0,0,0,0))",
    effect: "stained",
    ringPx: 4,
    glow: "0 0 12px rgba(217,164,65,.34), 0 0 26px rgba(63,127,184,.18)",
  },
  {
    id: "border_fosforo",
    label: "Fósforo",
    // A única peça que muda de INTENSIDADE ao longo de um ciclo longo (12s)
    // em vez de a cada segundo: acende forte e desbota, depois recarrega. É
    // tempo suficiente para a pessoa notar que a moldura estava mais clara há
    // um minuto — nenhuma outra faz isso.
    gradient: "linear-gradient(135deg, #1e2418, #2a3320, #1a2015)",
    sweep: "conic-gradient(from 0deg, #c6f24a, #8fd41f, #e4ff9a, #a8e236, #c6f24a)",
    effect: "charge",
    ringPx: 4,
    glow: "0 0 14px rgba(198,242,74,.5), 0 0 32px rgba(143,212,31,.22)",
  },
  // ── Onda 06/09 — pares das molduras com os temas de FUNDO ANIMADO ─────────
  //
  // Pedido do fundador: "seria legal criar molduras que combinassem com esses
  // temas, mas que fossem para ser comprados com moedas". São compráveis de
  // propósito — o tema é de assinatura ou de temporada, a moldura não: quem
  // não assina consegue a peça que conversa com ele.
  //
  // ⚠️ Só TRÊS, e as duas ausências são decisão medida:
  //  · `vitral` já tem par — a `border_vitral` nasceu em 31/08, antes do tema,
  //    e o vocabulário bate exato (gomos de cor separados por chumbo).
  //  · `sol` FICA DE FORA. Ouro e Brasa já ocupam o dourado e o alaranjado, e
  //    uma terceira peça quente repetiria o erro da Founder v1, reprovada em
  //    18/08 pelo motivo que o desenho não conseguia resolver: *"ela e a Ouro
  //    são a mesma cor"* — duas peças douradas com texturas diferentes
  //    continuam lendo como "as duas douradas" nos 30px da linha da liga.
  //
  // ⚠️ E nenhuma delas é só a paleta do tema num anel. A onda de 31/08 nasceu
  // porque as 5 primeiras eram todas gradiente, com a diferença entre Floral,
  // Oceano e Neon sendo MATIZ; estas trazem matéria própria (pétala, veludo,
  // nácar) e conversam com o tema pela cor, não são o tema.
  {
    id: "border_sakura",
    label: "Sakura",
    // Pétala sobre luz: o branco NÃO é vazio, é a translucidez da pétala
    // contra o sol — é o que separa "anel rosa" de "pétala". Par do tema
    // Sakura, cuja camada de perfil é justamente pétala caindo.
    //
    // 🔴 O tom PROFUNDO (#c62d63) não é escolha estética: sem ele a peça
    // sumia. Medido por pixel sobre o papel do próprio tema Sakura (#ffe4ee),
    // a 1ª versão tinha contraste MÁXIMO de 1,67 — o anel inteiro abaixo do
    // piso de 3:1 para elemento gráfico, e no caso de uso principal, que é
    // justamente ele acompanhando o tema. É a doença que este projeto já
    // registrou no --gold: a cor tentando se destacar de si mesma.
    //
    // ⚠️ E ele é o ACCENT do próprio tema Sakura, não um rosa novo: amarra a
    // peça ao tema em vez de inventar cor. Medido, passa 3:1 em papel claro E
    // escuro — 4,44 no Sakura, 4,71 no Linho, 3,56 no Noir —, o que importa
    // porque a moldura aparece em TODO tema (feed, liga), não só neste.
    gradient:
      "linear-gradient(135deg, #ffffff 0%, #ffd0e0 20%, #c62d63 46%, #ffe6ef 68%, #f2a0bd 100%)",
    glow: "0 0 10px rgba(242, 160, 189, 0.45), 0 0 22px rgba(255, 208, 224, 0.2)",
    effect: "glow",
  },
  {
    id: "border_alcova",
    label: "Alcova",
    // Veludo em penumbra: vinho profundo com o dourado do abajur entrando de
    // raspão. O par escuro da Sakura — a mesma flor à noite, que é como o tema
    // Alcova se relaciona com o Sakura na camada ambiente.
    gradient:
      "linear-gradient(135deg, #2a1922 0%, #5c3340 30%, #e59aa4 52%, #4a2b35 74%, #dcb079 100%)",
    glow: "0 0 10px rgba(229, 154, 164, 0.4), 0 0 24px rgba(220, 176, 121, 0.16)",
    effect: "glow",
  },
  {
    id: "border_lua",
    label: "Lua",
    // 🔴 REDESENHADA (09/09) — a 1ª versão pendurava um crescente no topo do
    // anel, e o fundador pegou na tela: *"o ícone na moldura tanto sol quanto
    // lua ficaram ruins"*. Ele estava certo, e a causa é de composição: um
    // símbolo colado num anel de 4px lê como ADESIVO em cima da moldura, não
    // como a moldura. A identidade tem de vir da FORMA do anel.
    //
    // 🌒 O que faz algo ler como lua não é o desenho de um crescente — é o
    // TERMINADOR, a linha que separa o lado iluminado do lado na sombra. É por
    // ela que se reconhece uma fase lunar numa foto sem nenhum contorno
    // desenhado. Aqui o anel INTEIRO é a lua vista de frente: um arco quase
    // branco, o terminador difuso, e o arco oposto na penumbra azul.
    //
    // ⚠️ A fase mora no `sweep` (a camada que gira), não no `gradient` base, e
    // isso é deliberado: o sweep é pintado POR CIMA do base, então uma fase no
    // base seria coberta e o trabalho não apareceria. Girando devagar, ela lê
    // como a lua mudando de fase — que é exatamente o gesto do tema.
    //
    // O `gradient` base continua nácar porque ele é o FALLBACK fiel: é o que
    // aparece no export do Studio (html-to-image não anima) e sob
    // `prefers-reduced-motion`, e ali um anel de madrepérola parado lê melhor
    // que uma fase congelada num ângulo arbitrário.
    gradient:
      "linear-gradient(135deg, #ece9fc 0%, #a8b6f0 26%, #d9d4f2 50%, #6f7fc4 74%, #ece9fc 100%)",
    // Terminador difuso entre 120° e 250°: a passagem da luz para a sombra é
    // longa de propósito (numa lua real ela é um degradê de dezenas de graus),
    // e o lado escuro NÃO é preto — é o azul profundo do próprio tema, porque
    // a face na sombra segue iluminada pela Terra.
    sweep:
      "conic-gradient(from 210deg, #ffffff 0deg, #f4f2ff 34deg, #d9d4f2 76deg, #a8b6f0 120deg, #6f7fc4 168deg, #3b3a72 214deg, #2a2a55 250deg, #4a4a8c 292deg, #a8b6f0 332deg, #ffffff 360deg)",
    glow: "0 0 12px rgba(168, 182, 240, 0.45), 0 0 26px rgba(236, 233, 252, 0.18)",
    // ⚠️ `sweep` e NÃO `aurora`: aquele é o teto da escada e é reservado à
    // Lendária, que custa 60 dias de ofensiva sem falhar — peça PAGA com ele
    // transformaria a conquista em segundo lugar. E sem `specular`, que é o que
    // distingue a Founder. Quem pega os dois é teste, não revisão.
    effect: "sweep",
    ringPx: 4,
  },
  {
    id: "border_pergaminho",
    label: "Pergaminho",
    requiresBadge: "dez_mil_paginas",
    // Conquistada (`dez_mil_paginas`). Fecha um buraco: `ofensiva_60` era a
    // ÚNICA badge do produto a conceder moldura — volume de leitura, por mais
    // alto que fosse, nunca virava peça de avatar.
    // ⚠️ A 1ª versão lia como "anel bege sujo": as manchas tinham contraste
    // baixo demais para uma faixa de 4px. É o CONTRASTE entre o pergaminho
    // claro e o queimado escuro que faz ler como borda chamuscada.
    gradient:
    // ⚠️ Sem `edge` (exclusivo da Founder). Como é ÉPICA, a escada cobra halo
    // E varredura: o halo é a luz de vela sobre o pergaminho, e a varredura é
    // essa luz passando. É o que a torna coerente com Vitral e Fósforo, que
    // custam o mesmo degrau.
      "conic-gradient(from 18deg, #f0e3bd 0deg 46deg, #4a3316 46deg 92deg, #f5ead0 92deg 146deg, #3d2a12 146deg 190deg, #ecdfb8 190deg 244deg, #54391a 244deg 296deg, #f0e3bd 296deg 360deg)",
    sweep:
      "conic-gradient(from 140deg, rgba(255,226,160,.5), rgba(0,0,0,0) 32%, rgba(255,226,160,.3) 66%, rgba(0,0,0,0))",
    effect: "stained",
    glow: "0 0 11px rgba(196,150,74,.4), 0 0 24px rgba(120,86,38,.18)",
    ringPx: 4,
  },
  {
    id: "border_diamante",
    label: "Diamante",
    requiresBadge: "liga_diamond_podio",
    // ── Conquistada na LIGA — substitui os "Louros" ──────────────────────
    // Os Louros foram reprovados pelo fundador vendo o render, e a medição
    // explica: 24 folhas a cada 13° medem 2,9px num avatar de 28px, então
    // viravam serrilha. E a metáfora não serve a um anel — coroa é um objeto
    // em C, com topo e base, não uma forma radialmente simétrica.
    //
    // Diamante é a pedra do próprio tier: 8 FACETAS de 45° (10px a 28px), com
    // salto duro entre faces claras e escuras, que é como uma pedra lapidada
    // reflete. É a única peça BRANCA do catálogo — não disputa leitura com o
    // ouro do Founder nem com o violeta da Lendária e do Cristal.
    //
    // ⚠️ Sem faíscas, como todas as outras: `aurora` é da Lendária, que custa
    // 60 dias de ofensiva sem falhar e precisa continuar sendo o teto de
    // camadas. Esta se separa por COR e por FACETA, não por intensidade.
    gradient:
      "conic-gradient(from 0deg, #dbeafe 0deg 45deg, #7aa7c7 45deg 90deg, #f8fbff 90deg 135deg, #9dc4dd 135deg 180deg, #e8f4ff 180deg 225deg, #6f9bbd 225deg 270deg, #ffffff 270deg 315deg, #a9cbe2 315deg 360deg)",
    sweep:
      "conic-gradient(from 0deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,.9) 26deg, rgba(255,255,255,0) 54deg, rgba(255,255,255,0) 200deg, rgba(214,240,255,.6) 224deg, rgba(255,255,255,0) 250deg)",
    // ⚠️ SEM `specular` e SEM `edge`: os dois são o acabamento exclusivo da
    // Founder (decisão de 18/08), e a 1ª versão desta peça os usava. O brilho
    // duro aqui vem das próprias FACETAS — o salto entre `#ffffff` e `#6f9bbd`
    // dentro do gradiente base —, que é como pedra lapidada reflete e não
    // depende de camada de acabamento nenhuma.
    effect: "facet",
    ringPx: 4,
    glow: "0 0 14px rgba(190,225,255,.65), 0 0 32px rgba(122,167,199,.28)",
  },
  {
    id: "border_lendaria",
    label: "Lendária",
    requiresBadge: "ofensiva_60",
    gradient: "linear-gradient(135deg, #a78bfa, #22d3ee, #a78bfa)",
    sweep:
      "conic-gradient(from 0deg, #6d28d9, #a78bfa, #e0f2fe, #22d3ee, #818cf8, #f0abfc, #a78bfa, #6d28d9)",
    glow: "0 0 13px rgba(167, 139, 250, 0.55), 0 0 32px rgba(34, 211, 238, 0.24)",
    effect: "aurora",
    ringPx: 4,
  },
  {
    id: "border_sol",
    label: "Sol",
    // 🔴 TERCEIRO desenho desta peça, e os dois anteriores foram reprovados por
    // razões diferentes — vale registrar as duas, porque cada uma fechou um
    // caminho:
    //
    //  1. **Disco solar pendurado no topo do anel** (emblema). O fundador pegou
    //     na tela: *"o ícone na moldura tanto sol quanto lua ficaram ruins"*.
    //     Símbolo colado num anel de 4px lê como ADESIVO, não como moldura.
    //  2. **Sunburst** (`repeating-conic-gradient`, 24 raios). Reprovado pelo
    //     `avatarBorderCatalogParity`, que já impunha piso de 18° por parada:
    //     no avatar de 28px o anel tem ~80px de circunferência, então 13° medem
    //     2,9px e o elemento lê como anel QUEBRADO (foi o que derrubou os
    //     "Louros", a "Marginália" e o latão v1). E respeitando o piso sobram
    //     10 raios — que, renderizados, viram um anel liso indistinguível da
    //     Ouro. O piso e o efeito são incompatíveis: sunburst não cabe aqui.
    //
    // ☀️🌑 O que ficou é o próprio nome da temporada: um ECLIPSE. A coroa quente
    // ao redor e um arco de SOMBRA atravessando — o disco da lua passando na
    // frente. É o mesmo vocabulário da `border_lua` (que tem uma fase), com o
    // sinal invertido: lá a luz é fria e a sombra azul; aqui a luz é quente e a
    // sombra é o miolo queimado.
    //
    // ⚠️ E é isto que resolve, enfim, a objeção que barrou a peça em 06/09
    // ("Ouro e Brasa já ocupam o dourado; uma terceira lê como mais uma
    // dourada"). Ouro e Brasa são anéis CONTÍNUOS — a diferença entre elas é
    // matiz, e matiz colapsa nos 30px da liga. Um anel com um arco escuro se
    // distingue por ESTRUTURA, que sobrevive ao tamanho pequeno: mesmo com a
    // cor achatada, "anel inteiro aceso" e "anel com um pedaço apagado"
    // continuam sendo coisas diferentes.
    gradient:
      "linear-gradient(135deg, #fff3dc 0%, #ffc46e 20%, #ba5009 46%, #f59e0b 70%, #ffe8cf 100%)",
    // A coroa gira e o arco de sombra viaja com ela. O escuro (#3d1a04) é o
    // âmbar do tema levado quase ao carvão, nunca preto puro: numa coroa real o
    // disco ocultante ainda recebe luz difusa da própria coroa.
    sweep:
      "conic-gradient(from 200deg, #fff3dc 0deg, #ffd9a0 30deg, #f59e0b 74deg, #ba5009 118deg, #6b2f06 156deg, #3d1a04 192deg, #6b2f06 226deg, #d4700c 268deg, #ffc46e 312deg, #fff3dc 360deg)",
    glow: "0 0 13px rgba(245, 158, 11, 0.5), 0 0 28px rgba(255, 196, 110, 0.2)",
    // ⚠️ `sweep`, nunca `aurora` (Lendária, 60 dias de ofensiva) nem
    // `specular` (Founder) — peça paga com qualquer um rebaixa uma conquista.
    // Há teste cobrando as duas.
    effect: "sweep",
    ringPx: 4,
  },
  // ── Temporada 1 (Eclipse, out–nov/2026) — prêmios, nunca vendidas ─────────
  { id: "border_temporada_2026t1_top10", label: "Eclipse · Top 10", requiresBadge: "temporada_2026t1_top10", ...ECLIPSE_T1_RING },
  { id: "border_temporada_2026t1_meta", label: "Eclipse · Constante", requiresBadge: "temporada_2026t1_meta", ...ECLIPSE_T1_RING },
  // ── Temporada 2 (Entardecer, dez/2026–jan/2027) — prêmios, nunca vendidas ──
  { id: "border_temporada_2026t2_top10", label: "Entardecer · Top 10", requiresBadge: "temporada_2026t2_top10", ...ENTARDECER_T2_RING },
  { id: "border_temporada_2026t2_meta", label: "Entardecer · Constante", requiresBadge: "temporada_2026t2_meta", ...ENTARDECER_T2_RING },
  // ── Platina (BACKLOG §0 item 12b F7) — de quem fecha as 45; nunca vendida ──
  // Mock `redesign-livra/cosmeticos-t1`, prancha Platina, opção P1 aprovada
  // (23/09): metal polido girando, ESCURO nas bordas (#4b5566) para não sumir
  // no papel claro. `sweep`, nunca `aurora`/`specular` (Lendária/Founder).
  {
    id: "border_platina_polida",
    label: "Platina polida",
    requiresBadge: "platina",
    gradient: "linear-gradient(135deg, #f3f5f9 0%, #8a94a6 45%, #e6eaf0 100%)",
    sweep:
      "conic-gradient(from 0deg, #ffffff 0deg, #dfe4ec 40deg, #8a94a6 90deg, #4b5566 140deg, #c9d1dd 190deg, #ffffff 230deg, #9aa4b5 280deg, #4b5566 320deg, #ffffff 360deg)",
    glow: "0 0 12px rgba(201, 209, 221, 0.55), 0 0 24px rgba(75, 85, 102, 0.18)",
    effect: "sweep",
    ringPx: 4,
  },
];

export const AVATAR_BORDERS_MAP: Record<string, AvatarBorderItem> = Object.fromEntries(
  AVATAR_BORDERS.map((b) => [b.id, b]),
);

/**
 * A moldura de Founder — também o anel PADRÃO de quem é fundador e não equipou
 * nada (ver `AvatarDisplay`).
 *
 * Até 13/08 existiam DUAS peças douradas de fundador no produto: esta, com
 * bisel canelado, luz fixa e aresta usinada, e um `linear-gradient` de duas
 * paradas girando com `animate-spin-slow` chumbado no `AvatarDisplay` — o
 * poste de barbearia que a 1ª versão desta moldura já tinha sido reprovada por
 * ser. Como o anel de tier aparece em TODA superfície que não passa moldura
 * equipada, era a versão ruim que o fundador via na maior parte do app.
 * Colapsar as duas num item só é o mesmo remédio do BUG-81/BUG-87: dois
 * catálogos da mesma coisa divergem, e divergiram.
 */
export const FOUNDER_BORDER_ID = "border_founder";

/**
 * "Não quero moldura nenhuma" — um estado EXPLÍCITO, distinto de `null`.
 *
 * Sem ele o Founder não conseguia desligar o latão: `activeAvatarBorder: null`
 * significa "nunca escolhi", e é justamente o caso que cai no fallback abaixo.
 * Desequipar devolvia a moldura que a pessoa estava tentando tirar.
 *
 * ⚠️ Não é id de `store_items` e nunca vai ao inventário. `setActiveCosmetic`
 * o trata como o `null` — remover o que está vestido não depende de posse.
 */
export const BORDER_NONE_ID = "none";

/**
 * Id da moldura que o avatar deve DESENHAR, dados a escolha da pessoa e o tier.
 *
 * É a única fonte da composição tier × moldura. Antes ela morava espalhada nos
 * call sites, em três dialetos que discordavam entre si:
 *   · `HomeHeaderRedesign`  → passava tier E moldura (o certo)
 *   · `ProfileHeroRedesign` → `proTier={avatarBorder ? null : tier}` (zerava o
 *     tier quando havia moldura, matando o halo composto de Founder)
 *   · `SettingsIndex`       → passava SÓ o tier, sem a moldura
 *
 * O terceiro é o BUG do print: o fundador equipou a Lendária (conquistada com
 * 60 dias de ofensiva) e o Settings desenhava o latão por cima dela, porque sem
 * `avatarBorder` a função cai no fallback de Founder. Regra que mora no
 * chamador é regra que cada chamador escreve de um jeito — a doença do
 * BUG-81/BUG-87.
 *
 * Precedência: escolha explícita > "nenhuma" > default de Founder > nada.
 */
export function resolveAvatarBorderId(
  activeBorderId: string | null | undefined,
  proTier: "pro" | "founder" | null | undefined,
): string | null {
  // Escolha explícita de não usar moldura. Vence o default de Founder — é para
  // isso que ela existe.
  if (activeBorderId === BORDER_NONE_ID) return null;

  // Id conhecido ganha de tudo. Id DESCONHECIDO (item removido do catálogo,
  // dado antigo) cai no fallback em vez de sumir com o anel: o avatar nunca
  // fica pior do que ficaria sem escolha nenhuma.
  if (activeBorderId && AVATAR_BORDERS_MAP[activeBorderId]) return activeBorderId;

  // Quem nunca escolheu e é fundador recebe o latão (decisão do fundador,
  // 18/08: default sim, imposto não).
  return proTier === "founder" ? FOUNDER_BORDER_ID : null;
}

/** Item completo da moldura, ou null se id ausente/desconhecido. */
export function avatarBorder(id?: string | null): AvatarBorderItem | null {
  if (!id) return null;
  return AVATAR_BORDERS_MAP[id] ?? null;
}

/**
 * Só o anel base. Segue exportado para consumidores que precisam de uma string
 * de cor (export estático, preview em superfície que não monta as camadas).
 * Quem renderiza moldura na UI deve usar `<AvatarBorderRing>`, que é onde o
 * tratamento por raridade vive — assim loja, "Meus Itens" e avatar nunca
 * divergem de novo.
 */
export function avatarBorderGradient(id?: string | null): string | null {
  return avatarBorder(id)?.gradient ?? null;
}

/** Espessura padrão do anel quando o item não declara `ringPx`. */
export const DEFAULT_BORDER_RING_PX = 3;

/**
 * Lado do avatar em que `ringPx` vale INTEIRO. Abaixo dele a espessura encolhe
 * proporcionalmente; acima, satura.
 *
 * 44px é o avatar do membro na liga privada e o piso de toque da casa — o menor
 * tamanho em que o anel cheio ainda lê como acabamento e não como contorno.
 */
export const BORDER_RING_FULL_AT_PX = 44;

/** Nunca menos que isto: abaixo de 2px o anel some no antisserrilhado. */
export const MIN_BORDER_RING_PX = 2;

/**
 * Espessura do anel PARA ESTE avatar.
 *
 * `ringPx` era absoluto — 4px valiam igual no hero de 112px e na linha da liga
 * de 28px. Medido, isso é **3,6% do lado no hero contra 14,3% na liga**: a
 * mesma peça lê como acabamento fino lá e como contorno grosso aqui, que é o
 * "as bordas ficam muito grossas com o profile menor" do relato (27/08).
 *
 * A escala só desce: em avatar grande a espessura é a desenhada, e abaixo de
 * `BORDER_RING_FULL_AT_PX` ela acompanha o lado. Sem o piso, a moldura de
 * 1200 moedas viraria um fio de 1px na busca e no feed.
 *
 * ⚠️ Meio pixel é de propósito: em DPR 2 e 3 ele existe no dispositivo, e
 * arredondar para inteiro devolveria o degrau que esta função veio tirar.
 *
 * @param {number|null|undefined} ringPx Espessura desenhada do item.
 * @param {number|null|undefined} sizePx Lado do avatar, em px CSS.
 * @return {number} Espessura a aplicar.
 */
export function resolveBorderRingPx(
  ringPx: number | null | undefined,
  sizePx: number | null | undefined,
): number {
  const base = ringPx ?? DEFAULT_BORDER_RING_PX;
  if (!sizePx || sizePx >= BORDER_RING_FULL_AT_PX) return base;
  const escalado = (base * sizePx) / BORDER_RING_FULL_AT_PX;
  return Math.max(MIN_BORDER_RING_PX, Math.round(escalado * 2) / 2);
}

/**
 * Classe da camada girante por efeito (definidas em `styles/domain.css`).
 * `glow` não tem varredura — só halo — e por isso mapeia para string vazia.
 */
export const BORDER_SWEEP_CLASS: Record<BorderEffect, string> = {
  glow: "",
  sweep: "lb-border-sweep",
  ember: "lb-border-ember",
  aurora: "lb-border-aurora",
  obsidian: "lb-border-obsidian",
  prism: "lb-border-prism",
  bleed: "lb-border-bleed",
  misprint: "lb-border-misprint",
  stained: "lb-border-stained",
  charge: "lb-border-charge",
  facet: "lb-border-facet",
};

/**
 * Faíscas do tratamento `aurora`: 3 pontos na circunferência, com raio e
 * posição em PORCENTAGEM — escalam sozinhas do avatar de 32px do header ao de
 * 112px do hero. Em px, ficariam grosseiras no pequeno e invisíveis no grande.
 *
 * ⚠️ A camada é montada com `inset` NEGATIVO (ver BORDER_SPARKS_INSET): com
 * `inset: 0` as faíscas caem dentro do anel e o miolo opaco do avatar come
 * quase tudo — sobra o que couber em 4px de anel, que a olho nu não lê como
 * faísca. Medido no preview renderizado antes de fechar a onda.
 */
// ⚠️ Cada centro fica a MAIS DE UM RAIO de todas as bordas, e isso é a regra
// que faz a faísca ser redonda. Até 18/08 elas eram `at 50% 2%` (raio 9%),
// `at 94% 72%` e `at 7% 67%` (raio 7%): com o centro a 2% do topo e raio 9%,
// **mais da metade do círculo cai fora da área pintável** — e background não
// vaza para fora do próprio elemento. O relato do fundador ("as bolinhas em
// volta sendo cortadas") não era overflow de ancestral nenhum: era a caixa da
// própria camada. As três estavam assim; a de cima, que é a mais visível, era
// a pior.
//
// Os centros novos ficam sobre um círculo de raio 37,9% — a borda externa do
// anel dentro da camada expandida —, com folga ≥ 12% para qualquer borda.
export const BORDER_SPARKS_BACKGROUND = [
  "radial-gradient(7% 7% at 50% 12%, #ffffff 0%, rgba(255,255,255,0.85) 28%, rgba(255,255,255,0) 70%)",
  "radial-gradient(6% 6% at 81% 72%, #ffffff 0%, rgba(255,255,255,0.85) 28%, rgba(255,255,255,0) 70%)",
  "radial-gradient(6% 6% at 19% 72%, #ffffff 0%, rgba(255,255,255,0.85) 28%, rgba(255,255,255,0) 70%)",
].join(",");

/**
 * Quanto a camada de faíscas extrapola o anel. Em %, para escalar com o
 * avatar — em px ficaria desproporcional nos 32px do header da Home.
 */
export const BORDER_SPARKS_INSET = "-16%";
